/**
 * AccountSheet — the account switcher: a full-screen frosted takeover with one
 * large card per identity, the active one wearing an animated shine border, plus
 * action cards (New organization / Log out) below.
 *
 * Extracted from MobileNavOverlay (the launcher's bottom-pill sheet) so the
 * share view's picker is the SAME switcher, not a lookalike — one set of cards,
 * one fog, one animation. The launcher renders it inline (it already owns a
 * portal + fog stack and measures the sheet to decide whether its pill fits);
 * standalone callers pass `portal` to get the wrapper + theme class for free.
 *
 * **It lived under `apps/web/src/mobile/` until the identity shell (§8.2) needed
 * it on wide screens too.** Wide-screen is a THIRD consumer of one design, not a
 * new one — so the file moved here rather than being imported across the mobile
 * boundary or reimplemented. Nothing about it was ever phone-specific: it is a
 * full-bleed list of cards, which is exactly what the multi-org picker wants.
 *
 * Styling stays in apps/web's index.css (.mobile-account-sheet /
 * .mobile-account-card / .mobile-shine-border / .ws-home-tile) — that is the
 * single source of truth for the look, shared with the launcher, and it must NOT
 * be forked for wide screens. The class names keep their `mobile-` prefix for
 * the same reason: renaming them would touch the launcher and the share view's
 * server-rendered copy of the same recipe (features/share-links/api/serve.ts)
 * for no behavioural gain.
 */
import { Fragment, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { LogOut, Building2, User } from 'lucide-react'
import { useTheme, getThemeClass } from './ThemeProvider'

/** One switchable identity: a tenant, or a (tenant, email) pair for orgs where
 *  the user holds several email identities. */
export interface IdentityRow {
  key: string
  tenantId: string
  /**
   * Which glyph the card wears — a person or a building, matching the taskbar's
   * AvatarMenu so the two switchers name the same thing the same way.
   *
   * Carried explicitly rather than inferred from `email` or `tag`: `email` is
   * absent for a single-default org as well as for personal, and `tag` is
   * display copy ("Personal" vs a role name) that a locale could translate out
   * from under the check. Required, so a fourth row builder has to state it.
   */
  kind: 'personal' | 'organization'
  /** Org email to switch as (undefined = personal / single default). */
  email?: string
  label: string
  sub?: string
  /** Right-aligned kind/role tag: "Personal" | "owner" | "member" | … */
  tag?: string
  seed: string
  initials: string
  active: boolean
  /** Marks the genuine "best access" identity (share view only) — a ★ next to
   *  the name, same designation the resolver resolves against. */
  best?: boolean
}

/** One identity card. The active one gets the warm fill + animated shine ring. */
export function IdentityCard({ row, onPick }: { row: IdentityRow; onPick: (row: IdentityRow) => void }) {
  return (
    <button
      onClick={() => onPick(row)}
      aria-label={`Switch to ${row.label}${row.email ? ` as ${row.email}` : ''}`}
      aria-current={row.active ? 'true' : undefined}
      className={`ws-home-tile mobile-account-card relative shrink-0 ${row.active ? 'is-selected' : ''}`}
    >
      {/* Selected = animated shine border, masked to the ring only. */}
      {row.active && <span className="mobile-shine-border" aria-hidden />}
      <span className="ws-project-glyph shrink-0">
        {row.kind === 'organization' ? <Building2 size={15} /> : <User size={15} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm themed-text">
          {row.label}
          {row.best && <span className="ml-1 text-amber-400" title="Best access">★</span>}
        </span>
        {row.sub && <span className="block truncate text-xs themed-text-muted">{row.sub}</span>}
      </span>
      {row.tag && <span className="shrink-0 text-[11px] themed-text-muted">{row.tag}</span>}
    </button>
  )
}

/** An action card (New organization / Log out). Same metrics as the identity
 *  cards so the glyph/text columns line up, but a solid surface — it isn't an
 *  identity, so it doesn't wear the glass tile. */
export function AccountActionCard({
  icon, label, danger, onClick,
}: {
  icon: React.ReactNode
  label: string
  danger?: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="mobile-account-card flex shrink-0 items-center gap-2.5 border themed-border themed-bg-secondary themed-bg-hover px-3.5 py-2.5 text-left active:themed-bg-tertiary"
    >
      <span className={`ws-project-glyph shrink-0 ${danger ? '!text-red-400' : ''}`}>{icon}</span>
      <span className={`text-sm ${danger ? 'text-red-400' : 'themed-text'}`}>{label}</span>
    </button>
  )
}

/**
 * The sheet body: the identity cards under GROUP labels, then whatever action
 * cards the caller passes as children. Tapping the fog / any empty area
 * dismisses.
 *
 * The labels name the two kinds of destination — "Personal" and
 * "Organizations" — rather than the one generic "Switch account" heading that
 * used to sit above the whole list. With the glyphs now saying which kind each
 * card is, a heading that says only what the sheet DOES leaves the grouping to
 * be inferred from the icons alone.
 *
 * **Each label renders only when its group is non-empty, and that is the point
 * of building them from the rows rather than hardcoding two headings.** An
 * identity-only person has no personal drive, so their personal group is empty
 * and its label must not appear over nothing — the callers already omit that
 * row (the launcher via `personalIsIdentityOnly`, the shell because an
 * identity-only tenant never enters `pickerTenants`). The same rule covers a
 * person with no organizations, which is every new account.
 *
 * `sheetRef` is forwarded so the launcher can keep measuring the list to decide
 * whether its bottom pill still fits.
 */
export function AccountSheet({
  rows, onPick, onClose, sheetRef, portal, children,
}: {
  rows: IdentityRow[]
  onPick: (row: IdentityRow) => void
  onClose: () => void
  sheetRef?: React.Ref<HTMLDivElement>
  /** Wrap in a body portal + theme class. The launcher already provides both
   *  (it lives in its own portal), so it renders inline instead. */
  portal?: boolean
  children?: React.ReactNode
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  // Personal first, then organizations — the order the rows already arrive in,
  // and the order both callers' taskbar menus use. A group with no rows drops
  // out entirely rather than rendering a heading over nothing.
  const groups = ([
    { kind: 'personal', label: 'Personal' },
    { kind: 'organization', label: 'Organizations' },
  ] as const)
    .map((g) => ({ ...g, rows: rows.filter((r) => r.kind === g.kind) }))
    .filter((g) => g.rows.length > 0)

  // Escape closes — but ONLY for standalone (portal) callers. The launcher peels
  // its layers itself (sheet first, then the launcher), and a second handler
  // here would close both on one keypress.
  useEffect(() => {
    if (!portal) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [portal, onClose])

  const body = (
    <div className="absolute inset-0 z-10">
      <div className="mobile-fog mobile-fog-deep mobile-fog-in absolute inset-0" onClick={onClose} aria-hidden />
      <div
        ref={sheetRef}
        onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
        className="mobile-sheet-in mobile-account-sheet absolute inset-0 flex w-full flex-col overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {groups.map((group) => (
          <Fragment key={group.kind}>
            <p className="mobile-switcher-label px-2 text-[10px] uppercase tracking-wide themed-text-muted">
              {group.label}
            </p>
            {group.rows.map((row) => <IdentityCard key={row.key} row={row} onPick={onPick} />)}
          </Fragment>
        ))}
        {children}
      </div>
    </div>
  )

  if (!portal) return body
  // ws-clear is LOAD-BEARING (same rule as the launcher): a theme class paints
  // an opaque --bg-primary, which would sit between the fog and the app — the
  // blur would then sample the wrapper's own paint and nothing would show
  // through. ws-clear keeps the wrapper transparent so the fog frosts the app.
  return createPortal(
    <div className={`${themeClass} ws-clear fixed inset-0 z-[200]`}>{body}</div>,
    document.body,
  )
}

/** The stock Log-out action card — every account sheet ends with one. */
export function LogOutCard({ onClick }: { onClick: () => void }) {
  return <AccountActionCard icon={<LogOut size={15} />} label="Log out" danger onClick={onClick} />
}
